/**
 * Screenshots — a horizontal, scroll-snap gallery of the product's workspaces.
 *
 * Each slide is a CSS browser-frame. When `SCREENSHOTS[i].imageUrl` is null it
 * renders a tasteful gradient skeleton mock (so nothing looks broken before real
 * captures exist); set `imageUrl` in landing-data.ts to a public asset path and
 * the same frame renders the real image in place — no code change needed.
 */

import { useRef } from 'react';

import { SCREENSHOTS, SCREENSHOTS_SECTION, type Screenshot } from '@/routes/landing/landing-data';
import { TONE_GRADIENT, SectionHeading } from './shared';

export function Screenshots() {
  const trackRef = useRef<HTMLDivElement>(null);

  const scrollByCard = (dir: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const amount = Math.min(track.clientWidth * 0.85, 460);
    track.scrollBy({ left: dir * amount, behavior: reduce ? 'auto' : 'smooth' });
  };

  return (
    <section id="apercu" className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <SectionHeading
            eyebrow={SCREENSHOTS_SECTION.eyebrow}
            title={SCREENSHOTS_SECTION.title}
            subtitle={SCREENSHOTS_SECTION.subtitle}
            align="left"
          />
          <div className="hidden shrink-0 gap-2 sm:flex">
            <CarouselBtn dir={-1} onClick={() => scrollByCard(-1)} label="Précédent" />
            <CarouselBtn dir={1} onClick={() => scrollByCard(1)} label="Suivant" />
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        className="mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth px-4 pb-4 sm:px-6 lg:px-8 [scrollbar-width:thin]"
      >
        {SCREENSHOTS.map((shot) => (
          <figure
            key={shot.title}
            className="w-[85vw] max-w-md shrink-0 snap-start sm:w-[26rem]"
          >
            <ScreenshotFrame shot={shot} />
            <figcaption className="mt-4 px-1">
              <p className="font-semibold text-foreground">{shot.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-muted">{shot.caption}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function CarouselBtn({ dir, onClick, label }: { dir: 1 | -1; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-token border border-border bg-surface text-foreground shadow-elevation-1 transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-5 w-5">
        {dir === -1 ? <path d="M15.75 19.5 8.25 12l7.5-7.5" /> : <path d="m8.25 4.5 7.5 7.5-7.5 7.5" />}
      </svg>
    </button>
  );
}

function ScreenshotFrame({ shot }: { shot: Screenshot }) {
  return (
    <div className="overflow-hidden rounded-token-xl border border-border bg-surface shadow-elevation-3">
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 border-b border-border bg-surface-2/60 px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
      </div>

      <div className="relative aspect-[16/10] w-full">
        {shot.imageUrl ? (
          <img
            src={shot.imageUrl}
            alt={`Karexia — ${shot.title}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        ) : (
          <SkeletonMock tone={shot.tone} title={shot.title} />
        )}
      </div>
    </div>
  );
}

/** A gradient skeleton stand-in for a real product screenshot. */
function SkeletonMock({ tone, title }: { tone: Screenshot['tone']; title: string }) {
  return (
    <div className="absolute inset-0 flex flex-col gap-3 bg-surface-2/40 p-4">
      <div aria-hidden="true" className={`h-9 rounded-token bg-gradient-to-r ${TONE_GRADIENT[tone]} opacity-90`} />
      <div className="grid flex-1 grid-cols-3 gap-3">
        <div className="col-span-1 space-y-2 rounded-token bg-surface p-3 shadow-elevation-1">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-2.5 rounded-full bg-muted/25" style={{ width: `${90 - i * 12}%` }} />
          ))}
        </div>
        <div className="col-span-2 flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-token bg-surface p-2 shadow-elevation-1">
                <div className={`mb-1.5 h-5 w-5 rounded bg-gradient-to-br ${TONE_GRADIENT[tone]}`} />
                <div className="h-2 w-full rounded-full bg-muted/25" />
              </div>
            ))}
          </div>
          <div className="flex-1 rounded-token bg-surface p-3 shadow-elevation-1">
            <svg viewBox="0 0 200 70" preserveAspectRatio="none" aria-hidden="true" className="h-full w-full">
              <path d="M0 55 L30 44 L60 50 L90 26 L120 34 L150 16 L200 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary/70" />
            </svg>
          </div>
        </div>
      </div>
      <span className="sr-only">Aperçu : {title}</span>
    </div>
  );
}
